import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const TravelerInfoStep = ({ onNext, onBack, bookingData, setBookingData }) => {
  const [travelers, setTravelers] = useState(bookingData?.travelers || []);
  const [contactInfo, setContactInfo] = useState(bookingData?.contactInfo || {
    email: '',
    phone: '',
    emergencyContact: '',
    emergencyPhone: ''
  });
  const [specialRequests, setSpecialRequests] = useState(bookingData?.specialRequests || '');
  const [createAccount, setCreateAccount] = useState(false);
  const [errors, setErrors] = useState({});

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'UK', label: 'United Kingdom' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'JP', label: 'Japan' },
    { value: 'SG', label: 'Singapore' }
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  // Initialize travelers based on booking data
  React.useEffect(() => {
    if (travelers?.length === 0) {
      const totalTravelers = (bookingData?.adults || 0) + (bookingData?.children || 0) + (bookingData?.infants || 0);
      const newTravelers = [];
      
      // Add adults
      for (let i = 0; i < (bookingData?.adults || 0); i++) {
        newTravelers?.push({
          id: `adult-${i}`,
          type: 'adult',
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          gender: '',
          nationality: '',
          passportNumber: '',
          passportExpiry: '',
          dietaryRequirements: ''
        });
      }
      
      // Add children
      for (let i = 0; i < (bookingData?.children || 0); i++) {
        newTravelers?.push({
          id: `child-${i}`,
          type: 'child',
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          gender: '',
          nationality: '',
          guardianName: '',
          dietaryRequirements: ''
        });
      }
      
      // Add infants
      for (let i = 0; i < (bookingData?.infants || 0); i++) {
        newTravelers?.push({
          id: `infant-${i}`,
          type: 'infant',
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          gender: '',
          nationality: '',
          guardianName: '',
          dietaryRequirements: ''
        });
      }
      
      setTravelers(newTravelers);
    }
  }, [bookingData, travelers?.length]);

  const updateTraveler = (travelerId, field, value) => {
    setTravelers(travelers?.map(traveler => 
      traveler?.id === travelerId 
        ? { ...traveler, [field]: value }
        : traveler
    ));
  };

  const updateContactInfo = (field, value) => {
    setContactInfo({ ...contactInfo, [field]: value });
  };

  const validateStep = () => {
    const newErrors = {};
    
    // Validate contact info
    if (!contactInfo?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(contactInfo?.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!contactInfo?.phone) {
      newErrors.phone = 'Phone number is required';
    }
    
    // Validate travelers
    travelers?.forEach((traveler, index) => {
      if (!traveler?.firstName) {
        newErrors[`${traveler.id}-firstName`] = 'First name is required';
      }
      if (!traveler?.lastName) {
        newErrors[`${traveler.id}-lastName`] = 'Last name is required';
      }
      if (!traveler?.dateOfBirth) {
        newErrors[`${traveler.id}-dateOfBirth`] = 'Date of birth is required';
      }
      if (!traveler?.nationality) {
        newErrors[`${traveler.id}-nationality`] = 'Nationality is required';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setBookingData({
        ...bookingData,
        travelers,
        contactInfo,
        specialRequests,
        createAccount
      });
      onNext();
    }
  };

  const getTravelerTitle = (traveler, index) => {
    const typeLabels = {
      adult: 'Adult',
      child: 'Child',
      infant: 'Infant'
    };
    
    const typeCount = travelers?.filter(t => t?.type === traveler?.type)?.indexOf(traveler) + 1;
    return `${typeLabels?.[traveler?.type]} ${typeCount}`;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Contact Information */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Contact Information
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Email Address"
                type="email"
                placeholder="your@email.com"
                value={contactInfo?.email}
                onChange={(e) => updateContactInfo('email', e?.target?.value)}
                error={errors?.email}
                required
              />
              
              <Input
                label="Phone Number"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={contactInfo?.phone}
                onChange={(e) => updateContactInfo('phone', e?.target?.value)}
                error={errors?.phone}
                required
              />
              
              <Input
                label="Emergency Contact Name"
                type="text"
                placeholder="Emergency contact name"
                value={contactInfo?.emergencyContact}
                onChange={(e) => updateContactInfo('emergencyContact', e?.target?.value)}
              />
              
              <Input
                label="Emergency Contact Phone"
                type="tel"
                placeholder="Emergency contact phone"
                value={contactInfo?.emergencyPhone}
                onChange={(e) => updateContactInfo('emergencyPhone', e?.target?.value)}
              />
            </div>
            
            <div className="mt-4">
              <Checkbox
                label="Create an account to save your information for future bookings"
                checked={createAccount}
                onChange={(e) => setCreateAccount(e?.target?.checked)}
              />
            </div>
          </div>

          {/* Traveler Information */}
          <div className="space-y-6">
            {travelers?.map((traveler, index) => (
              <div key={traveler?.id} className="bg-white rounded-xl border border-border p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {getTravelerTitle(traveler, index)}
                  </h3>
                  {index === 0 && (
                    <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
                      Lead Traveler
                    </span>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    type="text"
                    placeholder="First name"
                    value={traveler?.firstName}
                    onChange={(e) => updateTraveler(traveler?.id, 'firstName', e?.target?.value)}
                    error={errors?.[`${traveler?.id}-firstName`]}
                    required
                  />
                  
                  <Input
                    label="Last Name"
                    type="text"
                    placeholder="Last name"
                    value={traveler?.lastName}
                    onChange={(e) => updateTraveler(traveler?.id, 'lastName', e?.target?.value)}
                    error={errors?.[`${traveler?.id}-lastName`]}
                    required
                  />
                  
                  <Input
                    label="Date of Birth"
                    type="date"
                    value={traveler?.dateOfBirth}
                    onChange={(e) => updateTraveler(traveler?.id, 'dateOfBirth', e?.target?.value)}
                    error={errors?.[`${traveler?.id}-dateOfBirth`]}
                    required
                  />
                  
                  <Select
                    label="Gender"
                    options={genderOptions}
                    value={traveler?.gender}
                    onChange={(value) => updateTraveler(traveler?.id, 'gender', value)}
                    placeholder="Select gender"
                  />
                  
                  <Select
                    label="Nationality"
                    options={countryOptions}
                    value={traveler?.nationality}
                    onChange={(value) => updateTraveler(traveler?.id, 'nationality', value)}
                    placeholder="Select nationality"
                    error={errors?.[`${traveler?.id}-nationality`]}
                    required
                    searchable
                  />
                  
                  {traveler?.type === 'adult' && (
                    <>
                      <Input
                        label="Passport Number"
                        type="text"
                        placeholder="Passport number"
                        value={traveler?.passportNumber}
                        onChange={(e) => updateTraveler(traveler?.id, 'passportNumber', e?.target?.value)}
                      />
                      
                      <Input
                        label="Passport Expiry"
                        type="date"
                        value={traveler?.passportExpiry}
                        onChange={(e) => updateTraveler(traveler?.id, 'passportExpiry', e?.target?.value)}
                      />
                    </>
                  )}
                  
                  {(traveler?.type === 'child' || traveler?.type === 'infant') && (
                    <Input
                      label="Guardian Name"
                      type="text"
                      placeholder="Guardian's full name"
                      value={traveler?.guardianName}
                      onChange={(e) => updateTraveler(traveler?.id, 'guardianName', e?.target?.value)}
                    />
                  )}
                  
                  <Input
                    label="Dietary Requirements"
                    type="text"
                    placeholder="Any dietary restrictions or allergies"
                    value={traveler?.dietaryRequirements}
                    onChange={(e) => updateTraveler(traveler?.id, 'dietaryRequirements', e?.target?.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Special Requests */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Special Requests
            </h3>
            
            <textarea
              className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              rows={4}
              placeholder="Any special requests, accessibility needs, or additional information..."
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e?.target?.value)}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-border p-6 sticky top-32">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Information Required
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Mail" size={16} className="text-primary" />
                <span>Contact information</span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Users" size={16} className="text-primary" />
                <span>Traveler details</span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="FileText" size={16} className="text-primary" />
                <span>Travel documents</span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Shield" size={16} className="text-primary" />
                <span>Emergency contacts</span>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-foreground mb-1">Secure & Private</p>
                  <p className="text-muted-foreground">
                    Your information is encrypted and protected according to international privacy standards.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={onBack}
                className="flex-1"
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1"
                iconName="ArrowRight"
                iconPosition="right"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelerInfoStep;